import { RefObject, useEffect, useState } from 'react';
import Map from 'ol/Map';
import { Alert, Box, CircularProgress, Collapse, css, Icon, IconButton, Snackbar, Stack } from '@mui/material';
import ImageLayer from 'ol/layer/Image';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { TileWMS } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import { Control } from 'ol/control';
import { Options as ControlOptions} from 'ol/control/Control';
import LayersIcon from '@mui/icons-material/Layers';
import { Layer } from 'ol/layer';

import { useCreateMiniMap } from './hooks/useCreateMiniMap';
import { ILineString } from 'features/seismic/models/interfaces/ILineString';
import { GeopostWMSSource } from 'features/seismic/models/classes/layers/GeopostWMSSource';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';

const containerStyle = css({
    position: 'relative',
    width: '100%',
    height: '100%',
    '& .loading': {
        display: 'flex',
        position: 'fixed',
        zIndex: '2',
        width: 'calc(100% - 2px)',
        height: 'calc(100% - 38px)',
        backgroundColor: 'rgb(255, 255, 255)',
        opacity: 0.8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    '& .MuiButtonBase': {
        display: 'flex',
        alignItems: 'center',
    },
    '& .miniMap': {
        backgroundColor: 'rgb(4, 31, 60)',
        width: 'calc(100% - 2px)',
        height: 'calc(100% - 2px)',
        border: '1px solid #ffffff',
    }
});

type LayerSelectorControlOptions = ControlOptions & {
    onToggle: () => void
};

class LayerSelectorControl extends Control {
    /**
     * @param {Object} [opt_options] Control options.
     */
    constructor(opt_options: LayerSelectorControlOptions) {
        const options = opt_options || {};

        const button = document.createElement('button');
        button.innerHTML = 'L';

        const element = document.createElement('div');
        element.className = 'rotate-north ol-unselectable ol-control';
        element.appendChild(button);

        super({
            element: element,
            target: options.target,
        });

        button.addEventListener('click', options.onToggle, false);
    }
}

export function SeismicMiniMapContent({ opened, dataGeometryLine, allLayers, visibleLayers, hasGeometryError, mapSrid }: Props) {
    const { mapRef, isLoading, errorMessage } = useCreateMiniMap(opened, dataGeometryLine, allLayers, visibleLayers, hasGeometryError, mapSrid);
    const isLineGeometryLoading = useSeismicStore(state => state.isReLoadingLineGeometry);

    const [ showLineError, setShowLineError ] = useState(false);

    useEffect(() => {
        setShowLineError(!!errorMessage);
    }, [errorMessage]);

    return (
        <React.Fragment>
            <Box position='absolute'>
                <Snackbar
                    open={showLineError}
                    onClose={() => setShowLineError(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity='error'
                        action={
                            <IconButton
                                aria-label='close'
                                color='inherit'
                                size='small'
                                onClick={
                                    () => setShowLineError(false)
                                }
                            >
                                <CloseIcon fontSize='inherit'/>
                            </IconButton>
                        }
                    >
                        <strong> {errorMessage} </strong>
                    </Alert>
                </Snackbar>
            </Box>

            <Box css={containerStyle}>
                {(isLoading || isLineGeometryLoading) &&
                    <Box className='loading'>
                        <CircularProgress />
                    </Box>
                }

                <div ref={mapRef} style={{ backgroundColor: 'white' }} className='miniMap'></div>
            </Box>
        </React.Fragment>
    );
}

interface Props {
    opened: boolean;
    dataGeometryLine: ILineString | null;
    allLayers: Layer[];
    visibleLayers: Layer[];
    hasGeometryError: boolean;
    mapSrid: number;
}